import {
  ApiUtil,
  JsonApiUtils,
  readEntity,
  readLocal,
  selectEntity,
  readEntityById,
  displayError,
  displayErrorMessage,
  displaySuccessMessage,
  FAIcon,
} from 'reactifi';
import i18n from 'lib/utility/i18n';

const activeReminderErrorTitle = 'Cannot have more than one active reminder';

export function loadUserContent(programId, userId) {
  return async (dispatch, getState) => {
    await dispatch(
      readEntity(
        'user_contents',
        'external_access_token',
        { program_id: programId, user_id: userId },
        null,
        { number: 1, size: 200 }
      )
    );

    const {
      api: { user_content },
    } = getState();
    const progressIds = user_content
      ? [
        ...new Set(
          user_content.data
            .map((uc) => uc.attributes.progress_id)
            .filter((id) => id)
        ),
      ]
      : [];

    if (progressIds.length) {
      const filterParams = progressIds
        .map((id) => `filter[id][]=${id}`)
        .join('&');
      const progressUrl = `/api/data/progress.json?${filterParams}`;
      const progressData = await ApiUtil.loadApiData(progressUrl, false);

      dispatch(readLocal(progressData));
    }
  };
}

export function loadPlaylistItems(playlist_id) {
  return readEntity(
    'playlist_items',
    'playlist,content',
    { playlist_id },
    null,
    { number: 1, size: 100 }
  );
}

export function loadPersonalizedPlaylistItems(personalized_playlist_id) {
  return readEntity(
    'playlist_items',
    'personalized_playlist,content',
    { personalized_playlist_id },
    null,
    { number: 1, size: 100 }
  );
}

export function selectDiagnostic(dignosticId) {
  return selectEntity(dignosticId, 'diagnostics');
}

export function selectProgram(programId) {
  return selectEntity(programId, 'programs', ['account', 'text_customization']);
}

export function loadOtherPlaylists(program_id) {
  return readEntity('program_playlists', 'playlist,program.incentives', {
    program_id,
  });
}

export function loadOtherDiagnostics(program_id) {
  return readEntity('program_diagnostics', 'diagnostic', { program_id });
}

export function loadIncentives(program_id, playlist_id) {
  return readEntity('incentives', '', { program_id, playlist_id });
}

export function loadIncentiveActivities(
  program_id,
  playlist_id,
  user_id,
  incentive_id
) {
  return readEntity('incentive_activities', 'incentive', {
    program_id,
    playlist_id,
    user_id,
    incentive_id,
  });
}

export function loadAllIncentiveActivities(program_id, user_id) {
  return readEntity('incentive_activities', 'incentive', {
    program_id,
    user_id,
  });
}

export function loadGateways(program_id, promptCounts) {
  const queryParams = { program_id, ...promptCounts };
  const gatewaysUrl = `/api/data/gateways?${Object.entries(queryParams)
    .map(([key, value]) => `${key}=${value}`)
    .join('&')}`;

  return async (dispatch) => {
    const data = await ApiUtil.loadApiData(gatewaysUrl, false);
    dispatch(readLocal(data));
  };
}

export function loadCustomization(id) {
  return readEntityById(id, 'customizations');
}

export function loadUserPlaylist(playlistId) {
  const url = `/api/data/playlists/${playlistId}/user_playlist`;

  return async (dispatch) => {
    const data = await ApiUtil.loadApiData(url, false);
    dispatch(readLocal(data));
    return data;
  };
}

export function awardSharingPlaylist(playlistId) {
  const shareAwardUrl = `/api/data/playlists/${playlistId}/user_playlist/share`;

  return async (dispatch) => {
    const data = await ApiUtil.requestApiData(shareAwardUrl, false, 'PUT');
    dispatch(readLocal(data));
  };
}

export function getUserPlaylists(
  certificateServiceUrl,
  userId,
  playlistId,
  token
) {
  const baseUrl = `${certificateServiceUrl}/api/v1/users/${userId}/user_awards.json`;

  return async (dispatch) => {
    try {
      const url = JsonApiUtils.buildUrl(baseUrl, {
        filters: {
          domain_object: `playlists: ${playlistId}`,
        },
      });
      const data = await ApiUtil.loadExternalData(url, token, false);

      if (data) {
        dispatch(readLocal(data));
        return data?.data || [];
      }
    } catch (err) {
      dispatch(displayError(err));
    }
  };
}

export function setReminder(playlistId, programId, days, email) {
  const reminderUrl = `/api/data/playlists/${playlistId}/user_playlist/remind/${programId}`;
  const body = {
    data: {
      type: 'user_playlist_program_reminders',
      attributes: {
        email,
        remind_days: Number(days),
      },
    },
  };
  return async (dispatch) => {
    try {
      await ApiUtil.requestApiData(reminderUrl, false, 'POST', {
        body: JSON.stringify(body),
      });
      dispatch(
        displaySuccessMessage(
          <>
            <FAIcon icon="circle-check" color="white" />
            <strong>{i18n.t('Reminder set.')}</strong>{' '}
            {days === '1' ? i18n.t("We'll email you tomorrow.") : i18n.t("We'll email you in {{days}} days.", { days })}
          </>
        )
      );
    } catch (error) {
      if (error.response) {
        let payload;
        try {
          payload = await error.response?.json();
        } catch (_parseError) {
          dispatch(displayError(error));
          return;
        }
        const errorTitles = Array.isArray(payload?.errors) ? payload.errors.map(({ title }) => title) : [];

        if (errorTitles.includes(activeReminderErrorTitle)) {
          dispatch(displayErrorMessage(i18n.t('You already have an active reminder set. Please wait until it is delivered before setting a new one.')));
          return;
        }
      }

      dispatch(displayError(error));
    }
  };
}
